// frontend/src/components/settings/OperatingSchedulesTab.jsx
//
// Horarios OPERATIVOS de la flota.
//
// Gobiernan una sola cosa, y de forma explícita: si el equipo no opera ese día u
// hora, NO se generan tickets de "sensor sin reporte" ni "receptor sin reporte".
// Las alarmas de presión, temperatura y dual con lectura real se siguen
// evaluando siempre.
//
// Cascada de resolución: vehículo > tipo de vehículo > empresa > (sin configurar
// = opera siempre).

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';

import { api } from '../../services/api';

const WEEKDAYS = [
  ['operates_monday', 'Lun'],
  ['operates_tuesday', 'Mar'],
  ['operates_wednesday', 'Mié'],
  ['operates_thursday', 'Jue'],
  ['operates_friday', 'Vie'],
  ['operates_saturday', 'Sáb'],
  ['operates_sunday', 'Dom'],
];

const SCOPE_LABELS = {
  company: 'Empresa',
  vehicle_type: 'Tipo de vehículo',
  vehicle: 'Vehículo',
};

const EMPTY = {
  scope_type: 'company',
  vehicle_id: '',
  vehicle_type: '',
  name: '',
  active: true,
  start_time: '',
  end_time: '',
  timezone: '',
  notes: '',
  operates_monday: true,
  operates_tuesday: true,
  operates_wednesday: true,
  operates_thursday: true,
  operates_friday: true,
  // Por defecto se propone la jornada de lunes a viernes: es el caso que
  // motiva la función (no castigar el fin de semana sin operación).
  operates_saturday: false,
  operates_sunday: false,
};

const OperatingSchedulesTab = () => {
  const [schedules, setSchedules] = useState([]);
  const [canManage, setCanManage] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.operatingSchedules.getAll();
      setSchedules(res.data?.schedules || []);
      setCanManage(!!res.data?.can_manage);
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.detail || 'No se pudieron cargar los horarios operativos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    api.vehicles.getAll()
      .then((res) => setVehicles(res?.data?.items || res?.data || []))
      .catch(() => setVehicles([]));
  }, [load]);

  const vehicleTypes = useMemo(() => {
    const types = new Set();
    vehicles.forEach((v) => { if (v.type) types.add(v.type); });
    return Array.from(types).sort();
  }, [vehicles]);

  const vehicleName = useCallback((id) => {
    const found = vehicles.find((v) => String(v.id) === String(id));
    return found?.display_name || found?.alias || String(id || '').slice(0, 8);
  }, [vehicles]);

  const openNew = () => { setForm(EMPTY); setEditingId(null); setOpen(true); };
  const openEdit = (schedule) => {
    setForm({
      ...EMPTY,
      ...schedule,
      vehicle_id: schedule.vehicle_id || '',
      vehicle_type: schedule.vehicle_type || '',
      start_time: schedule.start_time || '',
      end_time: schedule.end_time || '',
      timezone: schedule.timezone || '',
      notes: schedule.notes || '',
    });
    setEditingId(schedule.id);
    setOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        vehicle_id: form.scope_type === 'vehicle' ? form.vehicle_id : null,
        vehicle_type: form.scope_type === 'vehicle_type' ? form.vehicle_type : null,
        start_time: form.start_time || null,
        end_time: form.end_time || null,
        timezone: form.timezone || null,
      };
      if (editingId) {
        await api.operatingSchedules.update(editingId, payload);
      } else {
        await api.operatingSchedules.create(payload);
      }
      toast.success('Horario operativo guardado');
      setOpen(false);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo guardar el horario');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (schedule) => {
    try {
      await api.operatingSchedules.remove(schedule.id);
      toast.success('Horario eliminado');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo eliminar el horario');
    }
  };

  const scopeLabel = (schedule) => {
    if (schedule.scope_type === 'vehicle') return `${SCOPE_LABELS.vehicle}: ${vehicleName(schedule.vehicle_id)}`;
    if (schedule.scope_type === 'vehicle_type') return `${SCOPE_LABELS.vehicle_type}: ${schedule.vehicle_type}`;
    return SCOPE_LABELS.company;
  };

  return (
    <Box>
      <Alert severity="info" icon={<ScheduleIcon />} sx={{ mb: 2 }}>
        Los horarios operativos evitan tickets de <strong>sin reporte</strong> cuando el equipo no
        tenía operación (típicamente el fin de semana). Al volver a horario, el tiempo sin reporte
        se mide desde el inicio real de la operación. Las alarmas de presión, temperatura y dual con
        lectura real se siguen evaluando siempre.
      </Alert>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
        {canManage && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={openNew}>
            Nuevo horario
          </Button>
        )}
      </Stack>

      <Paper variant="outlined">
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={26} /></Box>
        ) : schedules.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Sin horarios configurados: todos los equipos se consideran operativos siempre.
            </Typography>
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Alcance</TableCell>
                <TableCell>Días operativos</TableCell>
                <TableCell>Horario</TableCell>
                <TableCell>Zona horaria</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {schedules.map((schedule) => (
                <TableRow key={schedule.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{scopeLabel(schedule)}</Typography>
                    {schedule.name && (
                      <Typography variant="caption" color="text.secondary">{schedule.name}</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                      {WEEKDAYS.map(([key, label]) => (
                        <Chip key={key} size="small" label={label}
                          color={schedule[key] ? 'primary' : 'default'}
                          variant={schedule[key] ? 'filled' : 'outlined'} />
                      ))}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    {schedule.start_time && schedule.end_time
                      ? `${schedule.start_time} – ${schedule.end_time}`
                      : '24 h'}
                  </TableCell>
                  <TableCell>{schedule.timezone || 'Empresa'}</TableCell>
                  <TableCell>
                    <Chip size="small" color={schedule.active ? 'success' : 'default'}
                      label={schedule.active ? 'Activo' : 'Inactivo'} />
                  </TableCell>
                  <TableCell align="right">
                    {canManage && (
                      <>
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={() => openEdit(schedule)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar">
                          <IconButton size="small" color="error" onClick={() => handleDelete(schedule)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? 'Editar horario operativo' : 'Nuevo horario operativo'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <FormControl fullWidth size="small" disabled={!!editingId}>
              <InputLabel id="schedule-scope">Alcance</InputLabel>
              <Select
                labelId="schedule-scope"
                value={form.scope_type}
                label="Alcance"
                onChange={(e) => setForm({ ...form, scope_type: e.target.value })}
              >
                <MenuItem value="company">{SCOPE_LABELS.company}</MenuItem>
                <MenuItem value="vehicle_type">{SCOPE_LABELS.vehicle_type}</MenuItem>
                <MenuItem value="vehicle">{SCOPE_LABELS.vehicle}</MenuItem>
              </Select>
            </FormControl>

            {form.scope_type === 'vehicle' && (
              <FormControl fullWidth size="small" disabled={!!editingId}>
                <InputLabel id="schedule-vehicle">Vehículo</InputLabel>
                <Select
                  labelId="schedule-vehicle"
                  value={form.vehicle_id}
                  label="Vehículo"
                  onChange={(e) => setForm({ ...form, vehicle_id: e.target.value })}
                >
                  {vehicles.map((v) => (
                    <MenuItem key={v.id} value={v.id}>{v.display_name || v.alias}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {form.scope_type === 'vehicle_type' && (
              <FormControl fullWidth size="small" disabled={!!editingId}>
                <InputLabel id="schedule-type">Tipo de vehículo</InputLabel>
                <Select
                  labelId="schedule-type"
                  value={form.vehicle_type}
                  label="Tipo de vehículo"
                  onChange={(e) => setForm({ ...form, vehicle_type: e.target.value })}
                >
                  {vehicleTypes.map((type) => (
                    <MenuItem key={type} value={type}>{type}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <TextField label="Nombre (opcional)" size="small" value={form.name || ''}
              onChange={(e) => setForm({ ...form, name: e.target.value })} fullWidth />

            <Box>
              <Typography variant="caption" color="text.secondary">Días con operación</Typography>
              <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
                {WEEKDAYS.map(([key, label]) => (
                  <Chip
                    key={key}
                    label={label}
                    size="small"
                    color={form[key] ? 'primary' : 'default'}
                    variant={form[key] ? 'filled' : 'outlined'}
                    onClick={() => setForm({ ...form, [key]: !form[key] })}
                  />
                ))}
              </Stack>
            </Box>

            <Stack direction="row" spacing={2}>
              <TextField label="Desde (HH:MM)" size="small" value={form.start_time}
                placeholder="06:00"
                onChange={(e) => setForm({ ...form, start_time: e.target.value })} />
              <TextField label="Hasta (HH:MM)" size="small" value={form.end_time}
                placeholder="20:00"
                onChange={(e) => setForm({ ...form, end_time: e.target.value })} />
              <TextField label="Zona horaria" size="small" value={form.timezone}
                placeholder="America/Mexico_City"
                onChange={(e) => setForm({ ...form, timezone: e.target.value })} />
            </Stack>
            <Typography variant="caption" color="text.secondary">
              Deja el horario vacío para considerar operativo el día completo.
            </Typography>

            <TextField label="Notas" size="small" multiline minRows={2} value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })} fullWidth />

            <FormControlLabel
              control={<Switch checked={!!form.active}
                onChange={(e) => setForm({ ...form, active: e.target.checked })} />}
              label="Activo"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Guardando…' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OperatingSchedulesTab;
